import { SlotGridType } from 'api/definitions/slotdates/types';
import { SlotTypes } from 'constants/fulfilmentTypes';
import orderStatus from 'constants/orderStatus';
import { getCustomerAccount } from 'redux/modules/accounts/selectors/get-customer-account';
import { getPlaceOrderOrigin } from 'redux/modules/checkout/selectors/get-place-order-origin';
import { getPlaceOrderOriginMobileAvailable } from 'redux/modules/checkout/selectors/get-place-order-origin-mobile-available';
import { getSaveNewCard } from 'redux/modules/checkout/selectors/get-save-new-card';
import {
  customerSlotSelector,
  getCustomerSlotBranchId,
  getSlotType as getCustomerSlotType,
} from 'redux/modules/customer-slot/selectors';
import {
  getCustomerSlotEndDateTime,
  getCustomerSlotStartDateTime,
} from 'redux/modules/customer-slot/selectors/get-customer-slot-date-time';
import { getIsInstantCheckoutIsInProgress } from 'redux/modules/instant-checkout/selectors';
import { getOrder } from 'redux/modules/order/selectors';
import {
  getSlotBranchId as getOrderSlotBranchId,
  getSlotType as getOrderSlotType,
} from 'redux/modules/order/selectors/get-order-slot-selectors';
import {
  getEstimatedBalanceToPay,
  getEstimatedTotals,
} from 'redux/modules/order/selectors/get-order-totals';
import {
  getGiftCards,
  getGiftVouchers,
} from 'redux/modules/order/selectors/gift-cards-and-vouchers';
import { getPartnerDiscountCardNumber } from 'redux/modules/order/selectors/partner-discount';
import {
  getPromoCodesList,
  getQualifiedPromoCodesInline,
} from 'redux/modules/order/selectors/promo-codes';
import { getCustomerHasACompletedOrder } from 'redux/modules/orders/selectors';
import getFirstSavedPaymentCard from 'redux/modules/payment/selectors/get-first-saved-payment-card';
import { getCustomerOrderId, getCustomerOrderState } from 'redux/modules/sessions/selectors';
import { canInstantCheckout } from 'redux/modules/trolley/selectors/can-instant-checkout';
import { getTrolleyItems } from 'redux/modules/trolley/selectors/get-items';
import { getDeliveryCharge } from 'redux/modules/trolley/selectors/get-totals';
import { dayjs, differenceInDays, differenceInHours, FORMAT, format } from 'utils/date';
import { stashObject, unstashObject } from 'utils/stash';
import { getPaymentTypes } from '../utilities/get-payment-types';
import trolleyItemToAnalyticsProduct from '../utilities/trolley-item-to-analytics-product';

type EventType =
  | { event: 'transaction_completed'; [key: string]: any }
  | {
      event: 'redeem_gift_voucher';
      gift_voucher_number: string;
    }
  | { event: 'redeem_promo_code'; promo_code: string }
  | { event: 'redeem_gift_card'; gift_card_number: string };

function getOptionFieldValue(isAmend: boolean, isInstantCheckout: boolean) {
  if (isInstantCheckout) {
    return 'instant checkout amend';
  }

  return isAmend ? 'amend' : 'purchase';
}

function getSaveNewCardValue(
  isInstantCheckout: boolean,
  saveNewCard: boolean | undefined,
  isEntertainingCollectionSlot: boolean,
) {
  if (isEntertainingCollectionSlot) {
    return 'no_card';
  }

  if (isInstantCheckout || saveNewCard === undefined) {
    return 'using_saved_card';
  }

  return saveNewCard ? 'save_card' : 'dont_save_card';
}

type Product = {
  amendment_quantity_delta?: number;
  brand: string;
  category: string[];
  favourite?: 'YES' | 'NO';
  id: string;
  name: string;
  offer?: string;
  price: number;
  quantity: number;
  sponsored_source?: 'citrus_ad' | 'aem' | 'unknown';
  sponsored?: 'YES' | 'NO';
  variant: string;
};

type ServerSideAnalyticsOrderData = {
  order?: {
    items?: Product[];
    order_event_date: string;
    order_event_dow: string;
    order_event_hour: number;
    order_event_slot_date_diff: number;
    order_event_time: string;
    order_id?: string;
    purchase_or_amend?: string;
    service_type?: string;
    slot_date: string;
    slot_dow?: string;
    slot_duration: string;
    slot_hour?: number;
    slot_shipping_charge: number | undefined;
    slot_time: string;
    slot_type?: string;
    value?: number;
  };
  user?: {
    branch_id?: string | null;
    customer_id: string;
    customer_type: 'New' | 'Existing' | undefined;
    email_address: string;
  };
};

const SERVER_SIDE_ANALYTICS_ORDER_DATA = 'wtr_serverSideAnalyticsOrderData';

const getCustomerType = (customerHasACompletedOrder: boolean | undefined) => {
  if (customerHasACompletedOrder === undefined) {
    return undefined;
  }
  return customerHasACompletedOrder ? 'Existing' : 'New';
};

type StashServerSideAnalyticsOrderDataInput = {
  customerId: string;
  customerOrderId: string | undefined;
  customerType: 'Existing' | 'New' | undefined;
  deliveryCharge: number | undefined;
  email: string;
  estimatedBalanceToPay: number;
  optionField: string;
  orderEventDate: string;
  orderEventDayOfWeek: string;
  orderEventHour: number;
  orderEventSlotDateDiff: number;
  orderEventTime: string;
  orderSlotType: string | undefined;
  products: Product[];
  slotBranchId: string | null | undefined;
  slotDate: string;
  slotDayOfWeek: string;
  slotDuration: string;
  slotHour: number;
  slotTime: string;
  slotType: string;
};

function stashServerSideAnalyticsOrderData({
  customerId,
  customerOrderId,
  customerType,
  deliveryCharge,
  email,
  estimatedBalanceToPay,
  optionField,
  orderEventDate,
  orderEventDayOfWeek,
  orderEventHour,
  orderEventSlotDateDiff,
  orderEventTime,
  orderSlotType,
  products,
  slotBranchId,
  slotDate,
  slotDayOfWeek,
  slotDuration,
  slotHour,
  slotTime,
  slotType,
}: StashServerSideAnalyticsOrderDataInput) {
  const data: ServerSideAnalyticsOrderData = {
    order: {
      order_id: customerOrderId,
      value: estimatedBalanceToPay,
      purchase_or_amend: optionField,
      service_type: orderSlotType,
      slot_type: slotType,
      slot_hour: slotHour, // hour of the order
      slot_dow: slotDayOfWeek,
      slot_date: slotDate,
      slot_shipping_charge: deliveryCharge,
      slot_duration: slotDuration,
      slot_time: slotTime,
      order_event_date: orderEventDate,
      order_event_hour: orderEventHour,
      order_event_time: orderEventTime,
      order_event_dow: orderEventDayOfWeek,
      order_event_slot_date_diff: orderEventSlotDateDiff,
      items: products,
    },
    user: {
      customer_id: customerId,
      email_address: email,
      branch_id: slotBranchId,
      customer_type: customerType,
    },
  };
  stashObject(SERVER_SIDE_ANALYTICS_ORDER_DATA, data);
}

export function getExtraVirtualPageViewData(): ServerSideAnalyticsOrderData {
  const serverSideData = unstashObject(SERVER_SIDE_ANALYTICS_ORDER_DATA);
  return serverSideData ?? {};
}

function getSlotType(
  customerSlot: {
    slotType: string;
    greenSlot?: boolean;
    slotGridType?: string;
  },
  products: { variant?: string }[],
) {
  let type;
  if (customerSlot.slotType === SlotTypes.DELIVERY) {
    type = 'Delivery';
    if (customerSlot.greenSlot) {
      type = 'Delivery Green Slot';
    }
    if (customerSlot.slotGridType === SlotGridType.SAVER_GRID) {
      type = 'Delivery Saver Slot';
    }
  } else if (customerSlot.slotType === SlotTypes.GROCERY_COLLECTION) {
    type = 'Groceries Only Collection';
    if (products.some(product => product.variant === 'E')) {
      type = 'Groceries & Entertaining Collection';
    }
  } else if (customerSlot.slotType === SlotTypes.ENTERTAINING_COLLECTION) {
    type = 'Entertaining Only Collection';
  }
  return type ?? customerSlot.slotType;
}

export default (state: WtrState) => {
  // Depends on state: instantCheckout
  const isInstantCheckout = getIsInstantCheckoutIsInProgress(state);

  // Depends on state: trolley
  const trolleyItems = getTrolleyItems(state);
  const trolleyItemMapper = trolleyItemToAnalyticsProduct(state);
  const instantCheckoutEligibility = isInstantCheckout || canInstantCheckout(state);

  // Depends on state: trolley or order
  const deliveryCharge = isInstantCheckout
    ? getDeliveryCharge(state)
    : getEstimatedTotals(state)?.deliveryCharge?.amount;

  // Depends on state: order (only loaded in full checkout via initialise)
  const orderState = !isInstantCheckout ? getOrder(state) : undefined;
  const giftCards = orderState ? getGiftCards(state) : [];
  const giftVouchers = orderState ? getGiftVouchers(state) : [];
  const promoCodes = orderState ? getPromoCodesList(state) : [];
  const partnershipDiscount = orderState ? getPartnerDiscountCardNumber(state) : undefined;
  const placeOrderOrigin = isInstantCheckout ? '' : (getPlaceOrderOrigin(state) ?? '');
  let placeOrderOriginMobile;

  if (isInstantCheckout) {
    placeOrderOriginMobile = '';
  } else {
    placeOrderOriginMobile = getPlaceOrderOriginMobileAvailable(state)
      ? 'available'
      : 'unavailable';
  }

  const accountHasSavedCard = isInstantCheckout ? undefined : !!getFirstSavedPaymentCard(state);
  const estimatedBalanceToPay = isInstantCheckout ? 0 : getEstimatedBalanceToPay(state);
  // TODO: We need to check with analytics engineers if they want change this structure now that we have to support multiple promo codes
  const promoCodesInline = isInstantCheckout ? '' : getQualifiedPromoCodesInline(state);
  const paymentType = isInstantCheckout ? '' : getPaymentTypes(state);

  // Depends on state: accounts.account
  const { email, id: customerId } = getCustomerAccount(state);

  // Depends on state: sessions (or order)
  const customerOrderId = getCustomerOrderId(state) ?? orderState?.customerOrderId;
  const isAmend =
    isInstantCheckout ||
    (getCustomerOrderState(state) ?? orderState?.order?.status) === orderStatus.AMENDING;

  // Depends on state: customerSlot (or order)
  const slotBranchId = isInstantCheckout
    ? getCustomerSlotBranchId(state)
    : getOrderSlotBranchId(state);
  const orderSlotType = isInstantCheckout ? getCustomerSlotType(state) : getOrderSlotType(state);

  const saveNewCard = getSaveNewCardValue(
    isInstantCheckout,
    getSaveNewCard(state),
    orderSlotType === SlotTypes.ENTERTAINING_COLLECTION,
  );
  const products: Product[] = (trolleyItems && trolleyItems.map(trolleyItemMapper)) ?? [];
  const optionField = getOptionFieldValue(isAmend, isInstantCheckout);

  const customerSlot = customerSlotSelector(state);
  const slotStartDateTime = getCustomerSlotStartDateTime(state);
  const slotEndDateTime = getCustomerSlotEndDateTime(state);

  const slotDate = format(slotStartDateTime, FORMAT.ISO8601_DATE);
  const slotType = getSlotType(customerSlot, products);
  const slotDuration = `${differenceInHours(slotEndDateTime, slotStartDateTime)} hour slot`;
  const slotHour = Number(format(dayjs(slotStartDateTime).tz(), FORMAT.HOUR_24));
  const slotTime = `${format(dayjs(slotStartDateTime).tz())} - ${format(dayjs(slotEndDateTime).tz())}`;
  const slotDayOfWeek = format(slotStartDateTime, FORMAT.DAY_OF_WEEK);
  const currentDate = dayjs().tz();
  const orderEventDate = format(currentDate, FORMAT.ISO8601_DATE);
  const orderEventHour = Number(format(currentDate, FORMAT.HOUR_24));
  const orderEventTime = format(currentDate);
  const orderEventDayOfWeek = format(currentDate, FORMAT.DAY_OF_WEEK);
  const orderEventSlotDateDiff = differenceInDays(slotStartDateTime, currentDate);

  const giftCardEvents: EventType[] = giftCards.map(({ serialNumber }) => ({
    event: 'redeem_gift_card',
    gift_card_number: serialNumber,
  }));

  const giftVouchersEvents: EventType[] = giftVouchers
    .filter(({ status }) => status !== 'REDEEMED')
    .map(
      ({ serialNumber }) =>
        ({
          event: 'redeem_gift_voucher',
          gift_voucher_number: serialNumber,
        }) as const,
    );

  const promoCodesEvents: EventType[] = promoCodes
    .filter(({ status }) => status === 'APPLIED')
    .map(({ code }) => ({
      event: 'redeem_promo_code',
      promo_code: code,
    }));

  const partnershipDiscountEvent: EventType[] = partnershipDiscount
    ? [{ event: 'redeem_promo_code', promo_code: partnershipDiscount }]
    : [];

  const allEvents: EventType[] = [
    ...giftCardEvents,
    ...giftVouchersEvents,
    ...promoCodesEvents,
    ...partnershipDiscountEvent,
  ];

  allEvents.push({
    event: 'transaction_completed',
    instant_checkout_eligibility: instantCheckoutEligibility,
    orderDate: currentDate.toISOString(),
    user: {
      email_address: email,
      customerId,
    },
    ecommerce: {
      purchase: {
        actionField: {
          id: customerOrderId,
          affiliation: slotBranchId,
          revenue: estimatedBalanceToPay,
          shipping: deliveryCharge,
          coupon: promoCodesInline,
          option: optionField,
          service_type: orderSlotType,
          payment_type: paymentType,
          account_saved_card: accountHasSavedCard,
          save_new_card: saveNewCard,
          place_order_origin: placeOrderOrigin,
          place_order_origin_mobile: placeOrderOriginMobile,

          slot_date: slotDate,
          slot_type: slotType,
          slot_shipping_charge: deliveryCharge,
          slot_duration: slotDuration,
          slot_hour: slotHour,
          slot_time: slotTime,
          slot_dow: slotDayOfWeek,

          order_event_date: orderEventDate,
          order_event_hour: orderEventHour,
          order_event_time: orderEventTime,
          order_event_dow: orderEventDayOfWeek,
          order_event_slot_date_diff: orderEventSlotDateDiff,
        },
        products,
      },
    },
  });

  const customerType = getCustomerType(getCustomerHasACompletedOrder(state));

  stashServerSideAnalyticsOrderData({
    customerId,
    customerOrderId,
    customerType,
    deliveryCharge,
    email,
    estimatedBalanceToPay,
    optionField,
    orderEventDate,
    orderEventDayOfWeek,
    orderEventHour,
    orderEventSlotDateDiff,
    orderEventTime,
    orderSlotType,
    products,
    slotBranchId,
    slotDate,
    slotDayOfWeek,
    slotDuration,
    slotHour,
    slotTime,
    slotType,
  });

  return allEvents;
};
